import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Pencil, Plus, Power } from "lucide-react";
import { Suspense, useState } from "react";
import { ApiError } from "@/api/errors";
import { establishmentQuery } from "@/api/establishment";
import {
  activateProfessional,
  deactivateProfessional,
  type Professional,
  professionalsQuery,
} from "@/api/professionals";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProfissionalDialog } from "@/components/ProfissionalDialog";
import { PageActions } from "@/components/page-actions";
import { AusenciasSection } from "@/components/profissionais/AusenciasSection";
import { ExpedienteProfissionalSection } from "@/components/profissionais/ExpedienteProfissionalSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authed/configuracoes/profissionais")({
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      professionalsQuery(establishmentId),
      establishmentQuery(establishmentId),
    ]),
  staticData: {
    title: "Profissionais",
    subtitle: "Quem atende no estabelecimento — o bot oferece esses nomes no WhatsApp.",
  },
  component: ProfissionaisPage,
});

function ProfissionaisPage() {
  const { user } = useAuth();
  // `_authed` só renderiza autenticado — user nunca é null aqui.
  if (!user) return null;
  return (
    <ErrorBoundary titulo="Não foi possível carregar os profissionais">
      <Suspense fallback={<ProfissionaisSkeleton />}>
        <ProfissionaisContent establishmentId={user.establishment_id} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ProfissionaisSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

/**
 * `active` decide se o bot OFERECE o profissional — sem isso na tela, um
 * profissional afastado aparecia igualzinho aos outros e o dono seguia
 * achando que ele estava sendo ofertado no WhatsApp.
 */
function StatusProfissional({ active }: { active: boolean }) {
  if (active) return <Badge variant="outline">Ativo</Badge>;
  // Só "Inativo" no selo, com o efeito no `title`: escrito por extenso ele
  // alargava a coluna a ponto de empurrar as ações pra fora da tabela — e a
  // explicação completa já está no diálogo que desativa e no cartão do
  // celular, onde sobra largura.
  return (
    <Badge
      variant="secondary"
      className="text-muted-foreground"
      title="O bot não oferece este profissional e ele não entra no cálculo de disponibilidade."
    >
      Inativo
    </Badge>
  );
}

function AcoesProfissional({
  professional,
  busy,
  onHorarios,
  onEditar,
  onAtivar,
  onDesativar,
}: {
  professional: Professional;
  busy: boolean;
  onHorarios: () => void;
  onEditar: () => void;
  onAtivar: () => void;
  onDesativar: () => void;
}) {
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-9 gap-1.5 px-2 text-xs sm:h-7"
        onClick={onHorarios}
      >
        <CalendarClock className="h-3 w-3" />
        Horários
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-9 gap-1.5 px-2 text-xs sm:h-7"
        onClick={onEditar}
      >
        <Pencil className="h-3 w-3" />
        Editar
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-9 gap-1.5 px-2 text-xs sm:h-7"
        disabled={busy}
        onClick={professional.active ? onDesativar : onAtivar}
        aria-label={`${professional.active ? "Desativar" : "Ativar"} ${professional.name}`}
      >
        <Power className="h-3 w-3" />
        {busy ? "…" : professional.active ? "Desativar" : "Ativar"}
      </Button>
    </>
  );
}

function ProfissionaisContent({ establishmentId }: { establishmentId: string }) {
  const { data: professionals } = useSuspenseQuery(professionalsQuery(establishmentId));
  const { data: establishment } = useSuspenseQuery(establishmentQuery(establishmentId));

  // Diálogo único pros dois fluxos: `editing` preenchido = edição, undefined =
  // criação (mesmo padrão da tela de serviços).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Professional | undefined>(undefined);

  // Diálogo separado de horários/ausências: o de cadastro ficaria grande
  // demais com a grade semanal + lista de ausências dentro.
  const [agendaProfessional, setAgendaProfessional] = useState<Professional | null>(
    null,
  );

  // Desativar tem confirmação porque muda o que o BOT oferece: o
  // profissional some das opções novas na hora, e quem não repara continua
  // achando que ele está sendo ofertado. Reativar é inofensivo e vai
  // direto.
  const queryClient = useQueryClient();
  const [deactivating, setDeactivating] = useState<Professional | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  async function applyActive(p: Professional, active: boolean) {
    setToggleError(null);
    setTogglingId(p.id);
    try {
      await (active ? activateProfessional(p.id) : deactivateProfessional(p.id));
      setDeactivating(null);
      await queryClient.invalidateQueries({
        queryKey: ["establishment", establishmentId, "professionals"],
      });
    } catch (e) {
      setToggleError(
        e instanceof ApiError
          ? e.message
          : "Não foi possível mudar o status. Tente de novo.",
      );
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <>
      <PageActions>
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          {/* No celular o rótulo inteiro empurraria o título do cabeçalho pra
              fora: o + já diz "criar", e a tela em que se está diz o quê. */}
          Novo<span className="hidden sm:inline"> Profissional</span>
        </Button>
      </PageActions>

      <div>
        <div>
          {professionals.length === 0 ? (
            <div className="rounded-md border border-dashed bg-card px-6 py-12 text-center">
              <p className="font-medium">Nenhum profissional cadastrado</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Cadastre o primeiro profissional pra ele aparecer nas opções do bot e na
                agenda.
              </p>
              <Button
                size="sm"
                className="mt-4 gap-1.5"
                onClick={() => {
                  setEditing(undefined);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Novo Profissional
              </Button>
            </div>
          ) : (
            <>
              {toggleError && (
                <p role="alert" className="mb-3 text-sm text-destructive">
                  {toggleError}
                </p>
              )}

              {/* Abaixo de `md` a tabela vira cartão: num telefone de 390 ela
                  pedia 300px de rolagem lateral, e as duas ações ("Horários"
                  e "Editar") ficavam fora da tela — quando você rolava até
                  elas, o NOME do profissional é que tinha saído, então não
                  dava pra saber em qual linha se estava mexendo. */}
              <ul className="space-y-3 md:hidden">
                {professionals.map((p) => (
                  <li key={p.id} className="space-y-3 rounded-md border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          No bot: {p.nickname}
                          {p.specialty ? ` · ${p.specialty}` : ""}
                        </p>
                      </div>
                      <StatusProfissional active={p.active} />
                    </div>
                    {!p.active && (
                      <p className="text-xs text-muted-foreground">
                        Fora do bot — não é oferecido no WhatsApp nem entra na
                        disponibilidade.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      <AcoesProfissional
                        professional={p}
                        busy={togglingId === p.id}
                        onHorarios={() => setAgendaProfessional(p)}
                        onEditar={() => {
                          setEditing(p);
                          setDialogOpen(true);
                        }}
                        onAtivar={() => applyActive(p, true)}
                        onDesativar={() => setDeactivating(p)}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <div className="hidden rounded-md border bg-card md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Apelido (no bot)</TableHead>
                      <TableHead>Especialidade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {professionals.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>{p.nickname}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.specialty || "—"}
                        </TableCell>
                        <TableCell>
                          <StatusProfissional active={p.active} />
                        </TableCell>
                        <TableCell className="text-right">
                          <AcoesProfissional
                            professional={p}
                            busy={togglingId === p.id}
                            onHorarios={() => setAgendaProfessional(p)}
                            onEditar={() => {
                              setEditing(p);
                              setDialogOpen(true);
                            }}
                            onAtivar={() => applyActive(p, true)}
                            onDesativar={() => setDeactivating(p)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>

      <ResponsiveDialog
        open={deactivating !== null}
        onOpenChange={(open) => {
          if (!open && togglingId === null) setDeactivating(null);
        }}
      >
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Desativar {deactivating?.name}?
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              O bot deixa de oferecer este profissional no WhatsApp e ele some do
              cálculo de disponibilidade. Os agendamentos já marcados continuam de pé —
              cancele ou transfira cada um na agenda, se for o caso. Dá pra reativar
              quando quiser.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeactivating(null)}
              disabled={togglingId !== null}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deactivating && applyActive(deactivating, false)}
              disabled={togglingId !== null}
            >
              {togglingId !== null ? "Desativando…" : "Desativar"}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <ProfissionalDialog
        establishmentId={establishmentId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        professional={editing}
      />

      <ResponsiveDialog
        open={agendaProfessional !== null}
        onOpenChange={(open) => {
          if (!open) setAgendaProfessional(null);
        }}
      >
        <ResponsiveDialogContent className="sm:max-w-xl">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Horários de {agendaProfessional?.name}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Expediente próprio e ausências — valem só pra este profissional.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          {/* Remonta a cada profissional pra não vazar estado entre fichas.
              Suspense local: as seções usam useSuspenseQuery e abrir o dialog
              não pode derrubar a página inteira pro skeleton. */}
          {agendaProfessional && (
            <div key={agendaProfessional.id} className="space-y-6">
              {/* ErrorBoundary por seção: `Suspense` cobre o carregamento,
                  não a falha. Sem isto, um 500 aqui subia até o roteador e
                  trocava o painel inteiro por uma tela de erro em inglês —
                  uma consulta que quebra dentro de um diálogo não pode
                  levar a página junto. */}
              <ErrorBoundary titulo="Não foi possível carregar o expediente">
                <Suspense fallback={<Skeleton className="h-48 w-full" />}>
                  <ExpedienteProfissionalSection
                    establishmentId={establishmentId}
                    professionalId={agendaProfessional.id}
                  />
                </Suspense>
              </ErrorBoundary>
              <Separator />
              <ErrorBoundary titulo="Não foi possível carregar as ausências">
                <Suspense fallback={<Skeleton className="h-32 w-full" />}>
                  <AusenciasSection
                    professionalId={agendaProfessional.id}
                    timeZone={establishment.timezone}
                  />
                </Suspense>
              </ErrorBoundary>
            </div>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
